/**
 * Side-profile silhouettes of each vehicle type, drawn to a shared scale.
 *
 * Every silhouette uses the same viewBox — 52ft wide by 16ft tall, one user
 * unit per foot — and is anchored to the same ground line. Rendered at equal
 * widths they are therefore directly comparable: a 16ft car really does occupy
 * a third of the frame a 38ft fifth wheel fills.
 *
 * That comparability is the entire reason these are drawn rather than
 * photographed. A set of photographs is a set of unrelated framings and focal
 * lengths, and the one thing this product is about — whether the rig fits — is
 * the one thing they cannot show side by side.
 *
 * Dimensions come from VEHICLE_KINDS in lib/fit.js, so the drawing and the
 * numbers the search is run with cannot drift apart.
 */

const FRAME_FT = 52;
const FRAME_H = 16;
const GROUND = 15;
const X0 = 1;

/**
 * A wheel. The hub is punched out in white rather than drawn as a ring so it
 * still reads at card size, where the whole silhouette is about 80px tall and a
 * stroked ring collapses into a smudge.
 */
function Wheel({ cx, r = 1.25 }) {
  return (
    <>
      <circle cx={cx} cy={GROUND - r * 0.62} r={r} className="fill-current" />
      <circle cx={cx} cy={GROUND - r * 0.62} r={r * 0.42} className="fill-white" />
    </>
  );
}

/**
 * Each shape is a function of its real length and height in feet, so changing
 * a preset in lib/fit.js changes the drawing rather than leaving it stale.
 */
const SHAPES = {
  car: (l, h) => {
    const top = GROUND - h;
    return (
      <>
        <path d={`M${X0} ${GROUND - h * 0.45}
                  Q${X0} ${GROUND - h * 0.8} ${X0 + l * 0.18} ${GROUND - h * 0.82}
                  L${X0 + l * 0.3} ${top}
                  Q${X0 + l * 0.5} ${top - 0.2} ${X0 + l * 0.66} ${top}
                  L${X0 + l * 0.82} ${GROUND - h * 0.82}
                  Q${X0 + l} ${GROUND - h * 0.78} ${X0 + l} ${GROUND - h * 0.35}
                  L${X0 + l} ${GROUND - 0.5} L${X0} ${GROUND - 0.5} Z`}
              className="fill-current" />
        <Wheel cx={X0 + l * 0.24} r={0.95} /><Wheel cx={X0 + l * 0.78} r={0.95} />
      </>
    );
  },

  camper_van: (l, h) => (
    <>
      <path d={`M${X0} ${GROUND - h + 1.2} Q${X0} ${GROUND - h} ${X0 + 1.2} ${GROUND - h}
                L${X0 + l - 0.8} ${GROUND - h} Q${X0 + l} ${GROUND - h} ${X0 + l} ${GROUND - h + 1}
                L${X0 + l} ${GROUND - 0.6} L${X0} ${GROUND - 0.6} Z`} className="fill-current" />
      <rect x={X0 + l - 5} y={GROUND - h + 1} width={3.4} height={2.6} rx={0.4}
            className="fill-white opacity-70" />
      <Wheel cx={X0 + l - 3.2} /><Wheel cx={X0 + 3.4} />
    </>
  ),

  trailer: (l, h) => (
    <>
      <rect x={X0 + 1.5} y={GROUND - h} width={l - 1.5} height={h - 1.4} rx={0.4} className="fill-current" />
      <rect x={X0} y={GROUND - 2.6} width={l} height={1.1} rx={0.3} className="fill-current" />
      <path d={`M${X0} ${GROUND - 2.2} L${X0 - 0.9} ${GROUND - 1.4}`} strokeWidth={0.5}
            className="stroke-current" fill="none" strokeLinecap="round" />
      <Wheel cx={X0 + l * 0.6} r={0.9} />
    </>
  ),

  truck_camper: (l, h) => (
    <>
      {/* Cab */}
      <path d={`M${X0} ${GROUND - h * 0.45} Q${X0} ${GROUND - h * 0.62} ${X0 + 1.6} ${GROUND - h * 0.64}
                L${X0 + 2.6} ${GROUND - h * 0.82} L${X0 + 5.4} ${GROUND - h * 0.82}
                L${X0 + 5.4} ${GROUND - 0.6} L${X0} ${GROUND - 0.6} Z`} className="fill-current" />
      {/* Camper body over the bed, overhanging the cab */}
      <path d={`M${X0 + 4.2} ${GROUND - h} L${X0 + l} ${GROUND - h}
                L${X0 + l} ${GROUND - 2.4} L${X0 + 5.4} ${GROUND - 2.4}
                L${X0 + 5.4} ${GROUND - h * 0.72} L${X0 + 4.2} ${GROUND - h * 0.72} Z`}
            className="fill-current" />
      <Wheel cx={X0 + 3} /><Wheel cx={X0 + l - 3} />
    </>
  ),

  boat: (l, h) => (
    <>
      {/* Hull */}
      <path d={`M${X0 + 1} ${GROUND - h * 0.62} L${X0 + l} ${GROUND - h * 0.52}
                Q${X0 + l - 1} ${GROUND - 2.4} ${X0 + l - 5} ${GROUND - 2.2}
                L${X0 + 5} ${GROUND - 2.2} Z`} className="fill-current" />
      {/* Windshield / console */}
      <path d={`M${X0 + l * 0.42} ${GROUND - h * 0.6} L${X0 + l * 0.5} ${GROUND - h}
                L${X0 + l * 0.68} ${GROUND - h} L${X0 + l * 0.68} ${GROUND - h * 0.57} Z`}
            className="fill-current" />
      {/* Trailer */}
      <rect x={X0} y={GROUND - 1.9} width={l} height={0.7} rx={0.25} className="fill-current opacity-60" />
      <Wheel cx={X0 + l * 0.55} r={0.85} />
    </>
  ),

  travel_trailer: (l, h) => (
    <>
      <path d={`M${X0 + 2} ${GROUND - h + 1} Q${X0 + 2} ${GROUND - h} ${X0 + 3.2} ${GROUND - h}
                L${X0 + l - 1} ${GROUND - h} Q${X0 + l} ${GROUND - h} ${X0 + l} ${GROUND - h + 1}
                L${X0 + l} ${GROUND - 2.2} L${X0 + 2} ${GROUND - 2.2} Z`} className="fill-current" />
      <rect x={X0 + l - 6} y={GROUND - h + 1.2} width={3.6} height={2.4} rx={0.4}
            className="fill-white opacity-70" />
      {/* A-frame hitch */}
      <path d={`M${X0 + 2} ${GROUND - 2.6} L${X0} ${GROUND - 1.5} L${X0 + 2} ${GROUND - 2.2}`}
            className="fill-current" />
      <Wheel cx={X0 + l * 0.6} /><Wheel cx={X0 + l * 0.6 + 2.4} />
    </>
  ),

  motorhome: (l, h) => (
    <>
      <path d={`M${X0} ${GROUND - h + 2.4} Q${X0} ${GROUND - h} ${X0 + 2} ${GROUND - h}
                L${X0 + l - 1} ${GROUND - h} Q${X0 + l} ${GROUND - h} ${X0 + l} ${GROUND - h + 1}
                L${X0 + l} ${GROUND - 0.6} L${X0} ${GROUND - 0.6} Z`} className="fill-current" />
      {/* Windshield */}
      <path d={`M${X0 + 0.4} ${GROUND - h + 2.6} Q${X0 + 0.6} ${GROUND - h + 0.7} ${X0 + 2.4} ${GROUND - h + 0.6}
                L${X0 + 4.4} ${GROUND - h + 0.6} L${X0 + 4.4} ${GROUND - h + 4} L${X0 + 0.4} ${GROUND - h + 4} Z`}
            className="fill-white opacity-70" />
      <rect x={X0 + 7} y={GROUND - h + 1.4} width={l - 11} height={2.8} rx={0.4}
            className="fill-white opacity-45" />
      <Wheel cx={X0 + 3.4} r={1.15} /><Wheel cx={X0 + l - 4} r={1.15} />
    </>
  ),

  fifth_wheel: (l, h) => (
    <>
      {/* Raised gooseneck over the tow vehicle's bed */}
      <path d={`M${X0} ${GROUND - h + 3.6} L${X0 + 5} ${GROUND - h + 3.6}
                L${X0 + 5} ${GROUND - h + 1} Q${X0 + 5} ${GROUND - h} ${X0 + 6.4} ${GROUND - h}
                L${X0 + l - 1} ${GROUND - h} Q${X0 + l} ${GROUND - h} ${X0 + l} ${GROUND - h + 1}
                L${X0 + l} ${GROUND - 2.2} L${X0 + 5} ${GROUND - 2.2}
                L${X0 + 5} ${GROUND - h + 5.4} L${X0} ${GROUND - h + 5.4} Z`} className="fill-current" />
      <rect x={X0 + l - 7} y={GROUND - h + 1.4} width={4.2} height={2.8} rx={0.4}
            className="fill-white opacity-70" />
      <Wheel cx={X0 + l * 0.62} /><Wheel cx={X0 + l * 0.62 + 2.5} />
    </>
  ),
};

/**
 * @param {{kind:string,length:number,height:number,className?:string}} props
 * `length` and `height` are feet, and the drawing is anchored left so several
 * silhouettes stacked in a column line up on the same nose and ground line.
 */
export default function VehicleSilhouette({ kind, length, height, className = '' }) {
  const draw = SHAPES[kind] ?? SHAPES.travel_trailer;
  const l = Math.min(Number(length) || 20, FRAME_FT - 2);
  const h = Math.min(Number(height) || 10, FRAME_H - 3);

  return (
    <svg viewBox={`0 0 ${FRAME_FT} ${FRAME_H}`} className={className}
         preserveAspectRatio="xMinYMax meet" role="presentation" aria-hidden="true">
      {draw(l, h)}
      <line x1={0} y1={GROUND + 0.2} x2={FRAME_FT} y2={GROUND + 0.2}
            strokeWidth={0.18} className="stroke-current opacity-25" strokeLinecap="round" />
    </svg>
  );
}
